import {
    AfterViewInit,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import {Planning, SOURCES} from 'smag-commons/dto/msg';
import {TimeFilter, TimeFilterProfile, TimeFilterType} from '../time-filter/time-filter.component';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NService} from '../../i18n/i18n.service';
import {merge, of, Subject} from 'rxjs';
import {catchError, filter, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {SmagApiService} from '../../api/smag-api.service';
import {MsgBoxService} from '../msg-box.service';
import {PlanningStatus} from 'smag-commons/dto/planning';
import {getPage} from '../../commons/commons';

export interface PlanningNetwork {
    networkId: string;
    name?: string;
    plannable?: boolean;
    operatable?: boolean;
}

interface Plannings {
    plannings: Planning[];
    total: number;
}

const DEFAULT_TIME_FILTER: TimeFilterType = TimeFilterType.LAST_24_HOURS;
const PAGE_SIZE = 10;
const PAGE_SIZES = [10, 15, 30, 50];

@Component({
    selector: 'app-plannings',
    templateUrl: './plannings.component.html',
    styleUrls: ['./plannings.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Plannings per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class PlanningsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

    private destroyed = new Subject<void>();
    private timeFilter: TimeFilter;
    private reloadPlannings: Subject<void>;
    @ViewChildren('paginator', {emitDistinctChangesOnly: true}) private paginator: QueryList<MatPaginator>;

    @Input() network: PlanningNetwork;

    plannings: Plannings;

    // exported in HTML
    TimeFilterProfile = TimeFilterProfile;
    DEFAULT_TIME_FILTER = DEFAULT_TIME_FILTER;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;
    PlanningStatus = PlanningStatus;

    constructor(private readonly router: Router,
                private readonly i18n: I18NService,
                private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService) {
        this.timeFilter = null;
        this.reloadPlannings = new Subject<void>();
        this.plannings = null;
    }

    ngOnInit() {
    }

    ngAfterViewInit() {
        this.paginator.changes.pipe(
            map((query: QueryList<MatPaginator>) => query?.first),
            startWith(this.paginator.first),
            filter(paginator => !!paginator),
            switchMap(paginator => merge(
                paginator.page,
                this.reloadPlannings.pipe(
                    tap(() => paginator.firstPage())
                )
            ).pipe(
                startWith(undefined as unknown),
                map(() => [paginator.pageIndex, paginator.pageSize])
            )),
            takeUntil(this.destroyed)
        ).subscribe(([pageIndex, pageSize]) => {
            this.loadPlannings(pageIndex + 1, pageSize);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('network' in changes) {
            this.reloadPlannings.next();
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    trackByPlanningId(index: number, planning: Planning): string {
        return planning.id;
    }

    onSelectTimeFilter(timeFilter: TimeFilter) {
        this.timeFilter = timeFilter;
        this.reloadPlannings.next();
    }

    onClickGotoPlanning(planningId: string, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.router.navigate(['/c', 'ec', 'optimization', planningId], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    onClickNewPlanning() {
        this.apiService.planNetwork({network_id: this.network.networkId}, SOURCES.MANUAL)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Planning request submitted for {0}', this.network.name || this.network.networkId));
            })
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot submit planning request for {0}', this.network.name || this.network.networkId), err);
            });
    }

    private loadPlannings(pageIndex: number, pageSize: number) {
        const {fromTime, toTime} = this.timeFilter.range();

        this.plannings = null;
        this.apiService.retrievePlannings([this.network.networkId],
            fromTime,
            toTime,
            null,
            false).pipe(
            map(res => ({
                plannings: getPage(res.results, pageIndex - 1, pageSize),
                total: res.total
            } as Plannings)),
            catchError(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot retrieve plannings for {0}', this.network.name || this.network.networkId), err);
                return of({plannings: [], total: 0} as Plannings);
            })
        ).subscribe(plannings => {
            this.plannings = plannings;
        });
    }
}
